import { MILESTONES } from "../data/copy/milestones";

import { calculateStreak } from "./streak";
import { parseScheduleConfig } from "./schedule-config";

import type { MilestoneContext } from "../data/copy/milestones";
import type { Completion, Habit } from "../db/schema";
import type { CompletionRecord } from "./types";

/**
 * Check which milestones are currently achieved.
 * Returns array of milestone IDs.
 */
export function checkMilestones(
  habits: Habit[],
  completions: Completion[],
  today: string,
  firstDayOfWeek: number,
  graceDaysPerWeek: number,
): string[] {
  // Build per-habit completion maps
  const completionsByHabit = new Map<string, CompletionRecord[]>();
  for (const c of completions) {
    const existing = completionsByHabit.get(c.habit_id);
    const record: CompletionRecord = {
      effective_date: c.effective_date,
      type: c.type,
      value: c.value,
    };
    if (existing) {
      existing.push(record);
    } else {
      completionsByHabit.set(c.habit_id, [record]);
    }
  }

  // Compute current streaks for each habit
  const streaks = new Map<string, number>();
  for (const habit of habits) {
    const habitCompletions = completionsByHabit.get(habit.id) ?? [];
    const config = parseScheduleConfig(habit.schedule_type, habit.schedule_value);
    const streak = calculateStreak(
      config,
      habitCompletions,
      habit.created_at.slice(0, 10),
      today,
      firstDayOfWeek,
      graceDaysPerWeek,
    );
    streaks.set(habit.id, streak.current);
  }

  // Count total completions (done + backfill only)
  let totalCompletions = 0;
  for (const c of completions) {
    if (c.type === "done" || c.type === "backfill") {
      totalCompletions++;
    }
  }

  const ctx: MilestoneContext = { streaks, totalCompletions };

  return MILESTONES.filter((m) => m.check(ctx)).map((m) => m.id);
}

/**
 * Returns milestone IDs that are achieved but not yet dismissed.
 */
export function getNewMilestones(achieved: string[], dismissed: string[]): string[] {
  const dismissedSet = new Set(dismissed);
  return achieved.filter((id) => !dismissedSet.has(id));
}
